import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Plus, Edit, Trash2, Save, X, Eye, EyeOff, RotateCcw } from 'lucide-react';
import botMessageService, { BotMessage, CreateBotMessageRequest } from '../services/botMessageService';
import toast from 'react-hot-toast';
import { logger } from '../utils/logger'

const BotMessages: React.FC = () => {
  const navigate = useNavigate()
  const [messages, setMessages] = useState<BotMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMessage, setEditingMessage] = useState<BotMessage | null>(null);
  const [formData, setFormData] = useState<CreateBotMessageRequest>({
    message_key: '',
    message_text: '',
    variables: [],
    is_active: true
  });
  const [previewMessage, setPreviewMessage] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewVariables, setPreviewVariables] = useState<Record<string, string>>({});

  // Mensagens padrão do sistema
  const defaultMessages = [
    {
      key: 'welcome_message',
      name: 'Mensagem de Boas-vindas',
      description: 'Enviada quando o cliente inicia uma conversa',
      variables: ['name']
    },
    {
      key: 'menu_header',
      name: 'Cabeçalho do Menu',
      description: 'Título exibido no topo do menu de opções',
      variables: []
    },
    {
      key: 'menu_footer',
      name: 'Rodapé do Menu',
      description: 'Instruções exibidas no final do menu',
      variables: []
    },
    {
      key: 'no_menu_available',
      name: 'Sem Menu Disponível',
      description: 'Mensagem quando não há opções no menu',
      variables: []
    },
    {
      key: 'human_transfer_header',
      name: 'Cabeçalho Transferência Humana',
      description: 'Título da mensagem de transferência para atendente',
      variables: []
    },
    {
      key: 'human_transfer_body',
      name: 'Corpo Transferência Humana',
      description: 'Texto da mensagem de transferência para atendente',
      variables: ['menu_title']
    },
    {
      key: 'conversation_finished',
      name: 'Conversa Finalizada',
      description: 'Mensagem quando a conversa é finalizada',
      variables: []
    },
    {
      key: 'conversation_archived',
      name: 'Conversa Arquivada',
      description: 'Mensagem quando a conversa é arquivada',
      variables: []
    },
    {
      key: 'invalid_option',
      name: 'Opção Inválida',
      description: 'Mensagem quando o cliente escolhe uma opção inválida',
      variables: []
    },
    {
      key: 'waiting_input',
      name: 'Aguardando Informações',
      description: 'Mensagem quando aguarda input do cliente',
      variables: []
    },
    {
      key: 'incoming_call_unavailable',
      name: 'Chamada de voz indisponível',
      description: 'Enviada quando o cliente tenta uma ligação de voz (WhatsApp)',
      variables: []
    },
    {
      key: 'incoming_video_call_unavailable',
      name: 'Chamada de vídeo indisponível',
      description: 'Enviada quando o cliente tenta uma videochamada (WhatsApp)',
      variables: []
    }
  ];

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const response = await botMessageService.getMessages();
      if (response.success) {
        setMessages(response.data);
      }
    } catch (error) {
      logger.error('Erro ao carregar mensagens:', error);
      toast.error('Erro ao carregar mensagens');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDefaultMessages = async () => {
    try {
      const response = await botMessageService.createDefaultMessages();
      if (response.success) {
        toast.success('Mensagens padrão criadas com sucesso');
        loadMessages();
      }
    } catch (error) {
      logger.error('Erro ao criar mensagens padrão:', error);
      toast.error('Erro ao criar mensagens padrão');
    }
  };

  const handleOpenModal = (message?: BotMessage) => {
    if (message) {
      setEditingMessage(message);
      setFormData({
        message_key: message.message_key,
        message_text: message.message_text,
        variables: message.variables || [],
        is_active: message.is_active
      });
    } else {
      setEditingMessage(null);
      setFormData({
        message_key: '',
        message_text: '',
        variables: [],
        is_active: true
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingMessage(null);
    setFormData({
      message_key: '',
      message_text: '',
      variables: [],
      is_active: true
    });
    setPreviewMessage('');
    setShowPreview(false);
    setPreviewVariables({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingMessage) {
        const response = await botMessageService.updateMessage(editingMessage.message_key, formData);
        if (response.success) {
          toast.success('Mensagem atualizada com sucesso');
          loadMessages();
          handleCloseModal();
        }
      } else {
        const response = await botMessageService.createMessage(formData);
        if (response.success) {
          toast.success('Mensagem criada com sucesso');
          loadMessages();
          handleCloseModal();
        }
      }
    } catch (error: any) {
      logger.error('Erro ao salvar mensagem:', error);
      toast.error(error.response?.data?.message || 'Erro ao salvar mensagem');
    }
  };

  const handleDelete = async (messageKey: string) => {
    if (!window.confirm('Tem certeza que deseja deletar esta mensagem?')) {
      return;
    }

    try {
      const response = await botMessageService.deleteMessage(messageKey);
      if (response.success) {
        toast.success('Mensagem deletada com sucesso');
        loadMessages();
      }
    } catch (error: any) {
      logger.error('Erro ao deletar mensagem:', error);
      toast.error(error.response?.data?.message || 'Erro ao deletar mensagem');
    }
  };

  const handlePreview = async () => {
    if (!formData.message_text) return;

    try {
      const variables: Record<string, any> = {};
      formData.variables?.forEach(variable => {
        variables[variable] = previewVariables[variable] || `[${variable}]`;
      });

      const response = await botMessageService.processMessage(formData.message_key, variables);
      if (response.success) {
        setPreviewMessage(response.data.processed_message);
        setShowPreview(true);
      }
    } catch (error) {
      logger.error('Erro ao processar preview:', error);
      toast.error('Erro ao processar preview');
    }
  };

  const getMessageInfo = (messageKey: string) => {
    return defaultMessages.find(msg => msg.key === messageKey) || {
      name: messageKey,
      description: 'Mensagem personalizada',
      variables: []
    };
  };

  const addVariable = () => {
    setFormData(prev => ({
      ...prev,
      variables: [...(prev.variables || []), '']
    }));
  };

  const removeVariable = (index: number) => {
    setFormData(prev => ({
      ...prev,
      variables: prev.variables?.filter((_, i) => i !== index) || []
    }));
  };

  const updateVariable = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      variables: prev.variables?.map((v, i) => i === index ? value : v) || []
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-gray-200 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Mensagens do Bot
                </h1>
                <p className="text-gray-600">Personalize as mensagens automáticas do seu bot</p>
              </div>
            </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleCreateDefaultMessages}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Criar Mensagens Padrão
                </button>
                <button
                  onClick={() => handleOpenModal()}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nova Mensagem
                </button>
              </div>
          </div>
        </div>

        {messages.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma mensagem encontrada</h3>
            <p className="text-gray-500 mb-4">Crie mensagens personalizadas para o seu bot</p>
            <button
              onClick={handleCreateDefaultMessages}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Criar Mensagens Padrão
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {messages.map((message) => {
              const messageInfo = getMessageInfo(message.message_key);
              return (
                <div key={message.id} className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{messageInfo.name}</h3>
                      <p className="text-sm text-gray-600">{messageInfo.description}</p>
                      <div className="flex items-center mt-2">
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                          {message.message_key}
                        </span>
                        <span className={`ml-2 text-xs px-2 py-1 rounded ${
                          message.is_active 
                            ? 'bg-green-100 text-green-600' 
                            : 'bg-red-100 text-red-600'
                        }`}>
                          {message.is_active ? 'Ativa' : 'Inativa'}
                        </span>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleOpenModal(message)}
                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(message.message_key)}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{message.message_text}</p>
                  </div>
                  {message.variables && message.variables.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs text-gray-500 mb-2">Variáveis disponíveis:</p>
                      <div className="flex flex-wrap gap-1">
                        {message.variables.map((variable, index) => (
                          <span key={index} className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded">
                            {`${variable}`}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">
                  {editingMessage ? 'Editar Mensagem' : 'Nova Mensagem'}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Chave da Mensagem
                  </label>
                  <input
                    type="text"
                    value={formData.message_key}
                    onChange={(e) => setFormData(prev => ({ ...prev, message_key: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="ex: welcome_message"
                    required
                    disabled={!!editingMessage}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Identificador único para a mensagem (não pode ser alterado)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Texto da Mensagem
                  </label>
                  <textarea
                    value={formData.message_text}
                    onChange={(e) => setFormData(prev => ({ ...prev, message_text: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={6}
                    placeholder="Digite a mensagem aqui. Use ${variavel} para variáveis dinâmicas."
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Use <code>{'${nome}'}</code> para variáveis dinâmicas
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Variáveis
                  </label>
                  <div className="space-y-2">
                    {formData.variables?.map((variable, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={variable}
                          onChange={(e) => updateVariable(index, e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Nome da variável"
                        />
                        <button
                          type="button"
                          onClick={() => removeVariable(index)}
                          className="p-2 text-red-600 hover:text-red-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addVariable}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      + Adicionar Variável
                    </button>
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                    Mensagem ativa
                  </label>
                </div>

                <div className="flex justify-between pt-4">
                  <button
                    type="button"
                    onClick={handlePreview}
                    className="flex items-center px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    {showPreview ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                    {showPreview ? 'Ocultar Preview' : 'Preview'}
                  </button>
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Salvar
                    </button>
                  </div>
                </div>
              </form>

              {showPreview && previewMessage && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Preview da Mensagem:</h3>
                  <div className="bg-white p-3 rounded border">
                    <p className="text-sm whitespace-pre-wrap">{previewMessage}</p>
                  </div>
                  {formData.variables && formData.variables.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs text-gray-500 mb-2">Valores das variáveis para preview:</p>
                      <div className="space-y-1">
                        {formData.variables.map((variable, index) => (
                          <div key={index} className="flex items-center space-x-2">
                            <span className="text-xs text-gray-600">{variable}:</span>
                            <input
                              type="text"
                              value={previewVariables[variable] || ''}
                              onChange={(e) => setPreviewVariables(prev => ({ ...prev, [variable]: e.target.value }))}
                              className="text-xs px-2 py-1 border border-gray-300 rounded"
                              placeholder={`Valor para ${variable}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BotMessages;
